import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import { getToken } from "../../utils/tokenUtils";
import { Link } from "react-router-dom";
import Notification from "../../components/UI/Notification";
import { AuthContext } from "../../contexts/AuthContext";
import { getRoleBasedRoute } from "../../utils/routeUtils";

const LeaveBalance = () => {
  const [leaveData, setLeaveData] = useState(null);
  const [notification, setNotification] = useState(null);
  const [userId, setUserId] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const token = getToken();
  const { roles } = useContext(AuthContext);
  const getRoleBasedRouteLocal = (route) => getRoleBasedRoute(route, roles);

  // fetch leave details for the logged-in user and also for the user with the given ID
  const fetchLeaveDetails = async (id) => {
    setLoading(true);
    setError(null);
    try {
      const url = "https://hrmsnew.prepstripe.com/api/user/leave-details";
  
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };
  
      const response = await axios.post(url, { employee_id: id }, config);
  
      const filteredData = Object.fromEntries(
        Object.entries(response.data.data || {}).filter(
          ([key]) => !["created_at", "updated_at", "is_on_notice", "user_name", "user_id", "id"].includes(key)
        )
      );
      
      setNotification({
        message: `Leave details fetched successfully for user ID: ${id}`,
        type: "success"
      });
      
      setLeaveData(filteredData);
    } catch (err) {
      setError("Failed to fetch leave details.");
      setLeaveData(null);
      setNotification({
        message: "Failed to fetch leave details. Please check the employee ID.",
        type: "error"
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleReset = () => {
    setUserId("");
    setLeaveData(null);
    setError(null);
  };

  // Remove the useEffect that automatically fetches data on load
  return (
    <div className="p-4 sm:p-6 bg-white dark:bg-gray-900 shadow-lg rounded-lg">
            
      {/* notification compo */}
      {notification && (
          <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}

      {/* Breadcrumb */}
      <nav className="flex flex-wrap mb-4" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-2 rtl:space-x-reverse">
          <li>
            <div className="flex items-center">
              <Link
                to={getRoleBasedRouteLocal("/admin/attendance_management")}
                className="ms-1 text-sm sm:text-md font-medium text-gray-700 hover:text-blue-600 md:ms-2 dark:text-blue-400 dark:hover:text-blue-500">
                Attendance Management
              </Link>
            </div>
          </li>
          <li aria-current="page">
            <div className="flex items-center">
              <svg className="w-3 h-3 text-gray-400 mx-1" aria-hidden="true" fill="none" viewBox="0 0 6 10">
                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 9 4-4-4-4"/>
              </svg>
              <span className="ms-1 text-sm sm:text-md font-medium text-gray-500 md:ms-2 dark:text-gray-400">Check Leave Balances</span>
            </div>
          </li>
        </ol>
      </nav>
      
      <h2 className="text-lg sm:text-xl dark:text-white font-semibold mb-4">Check Leave Balances</h2>
      
      <div className="mb-4 flex space-x-2">
        <input
          id="userId"
          type="text"
          placeholder="Enter User ID"
          value={userId}
          onChange={(e) => {
            const value = e.target.value;
            // Allow only numbers and up to 5 digits
            if (/^\d{0,5}$/.test(value)) {
              setUserId(value);
            }
          }}
          className="px-4 py-2 dark:bg-black dark:text-white dark:border-gray-700 dark:focus:outline-none border rounded-lg w-full"
          inputMode="numeric"
          pattern="\d*"
          maxLength={5}
        />
        <button
          onClick={() => {
            if (userId.length === 5) {
              fetchLeaveDetails(userId);
            } else {
              alert("User ID must be exactly 5 digits.");
            }
          }}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
        >
          Search
        </button>
        {userId && (
          <button
            onClick={handleReset}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
          >
            Reset
          </button>
        )}
      </div>

      
      {/* {error && <p className="text-red-500">{error}</p>} */}
      {loading ? (
        <p className="text-gray-600">Loading...</p>
      ) : leaveData ? (
        <div className="overflow-x-auto bg-white shadow-md rounded-lg">
          <table className="min-w-full table-auto md:table-fixed text-sm">
            <thead className="bg-gray-100 dark:bg-gray-800 dark:text-white">
              <tr>
                <th className="py-2 px-4 dark:text-white text-left text-xs md:text-sm text-gray-700 whitespace-nowrap">Leave Type</th>
                <th className="py-2 px-4 dark:text-white text-left text-xs md:text-sm text-gray-700 whitespace-nowrap">Balance</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(leaveData).map(([key, value]) => (
                <tr key={key} className="dark:border-gray-700 dark:hover:bg-gray-950 dark:hover:text-white dark:text-white">
                  <td className="py-2 px-4 text-xs md:text-sm whitespace-nowrap">{key.replace(/_/g, ' ')}</td>
                  <td className="py-2 px-4 text-xs md:text-sm whitespace-nowrap">{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="py-4 px-4 text-center text-gray-500">No leave data available.</p>
      )}
    </div>
  );
};

export default LeaveBalance;
